/**
 * # Featured carousel — Alpine data factory
 *
 * A "featured + rail" carousel: the **active** slide always occupies the wide
 * slot on the left, and advancing promotes the *next* slide into that wide slot
 * while the rail shifts one step left — so exactly two boxes stay on screen
 * (wide 8-col + narrow 4-col). Used by `modreports_main`.
 *
 * ## Why this is layout-free JS
 * Unlike the scroll-snap {@link carousel}, the slide *widths change* with the
 * active index, so a native-scroll model can't express it. Instead the track is
 * a flex row translated by transform. To avoid any pixel measuring or resize
 * bookkeeping, **all** geometry lives in CSS via two custom properties:
 *
 * - `--narrow` — a non-active slide's width (responsive: full on mobile,
 *   `calc((100% - gap)/3)` from `nav:` up). The active slide widens to
 *   `calc((100% - gap)*2/3)` via the `.is-current` class.
 * - `--ci` — the current index, the *only* thing this component writes.
 *
 * The track transform is `translateX(calc(-1 * var(--ci) * (var(--narrow) +
 * var(--gap))))`: every slide left of the active one is narrow, so the active
 * slide's offset is exactly `current` narrow-steps. Percentages resolve against
 * the track's own width (which equals the clipping viewport), matching the
 * slides' `flex-basis` percentages — so it stays correct across breakpoints and
 * resizes with no JS. CSS transitions on `transform` (track) and `flex-basis`
 * (slides) animate the morph.
 *
 * ## Touch swipe
 * Because the track is transform-positioned (no native scroll), horizontal
 * swiping is wired up explicitly: a **touch** pointer drag past
 * {@link SWIPE_THRESHOLD} advances one step (left → `next`, right → `prev`).
 * The track carries `touch-action: pan-y`, so vertical drags scroll the page
 * normally (the browser then fires `pointercancel` and we abort the swipe);
 * only horizontal gestures reach us. Mouse/pen pointers are ignored so link
 * clicks are never swallowed — desktop uses the arrows.
 *
 * ## Markup contract
 * - Root: `x-data="featuredCarousel"`.
 * - Scroll/transform track: `x-ref="track"`, holding the slides; reads `--ci`.
 *   Must carry `touch-action: pan-y` (`touch-pan-y`) for the swipe to coexist
 *   with vertical page scroll.
 * - Each slide: `data-slide`; gets `.is-current` toggled on the active one.
 *
 * ## Exposed state / API (shared with `item_gallery_nav`)
 * - `current` — active (wide) slide index
 * - `count` — number of slides
 * - `atStart` / `atEnd` — booleans for disabling the prev/next arrows
 * - `prev()` / `next()` / `goTo(i)` — advance the rail one step / jump
 * - `isActive(i)` — `current === i`
 */

/** Minimum horizontal touch travel (px) that counts as a swipe, not a tap. */
const SWIPE_THRESHOLD = 40;

export interface FeaturedCarouselComponent {
  current: number;
  count: number;
  atStart: boolean;
  atEnd: boolean;
  _track: HTMLElement | null;
  _slides: HTMLElement[];
  _startX: number | null;
  _onDown: (e: PointerEvent) => void;
  _onUp: (e: PointerEvent) => void;
  _onCancel: () => void;
  init(): void;
  destroy(): void;
  _maxIndex(): number;
  _apply(): void;
  goTo(index: number): void;
  next(): void;
  prev(): void;
  isActive(index: number): boolean;
}

export function featuredCarousel(): FeaturedCarouselComponent {
  return {
    current: 0,
    count: 0,
    atStart: true,
    atEnd: false,

    _track: null,
    _slides: [],
    _startX: null,
    _onDown: () => {},
    _onUp: () => {},
    _onCancel: () => {},

    init(): void {
      const track = (this as unknown as { $refs: Record<string, HTMLElement> }).$refs.track;
      if (!track) {
        return;
      }
      this._track = track;
      this._slides = Array.from(track.querySelectorAll<HTMLElement>('[data-slide]'));
      this.count = this._slides.length;
      this._apply();

      // Touch swipe — only touch pointers, so mouse/pen link clicks pass through.
      this._onDown = (e: PointerEvent): void => {
        if (e.pointerType !== 'touch') {
          return;
        }
        this._startX = e.clientX;
        track.setPointerCapture(e.pointerId);
      };
      this._onUp = (e: PointerEvent): void => {
        if (this._startX === null) {
          return;
        }
        const dx = e.clientX - this._startX;
        this._startX = null;
        if (Math.abs(dx) >= SWIPE_THRESHOLD) {
          if (dx < 0) {
            this.next();
          } else {
            this.prev();
          }
        }
      };
      this._onCancel = (): void => {
        this._startX = null;
      };
      track.addEventListener('pointerdown', this._onDown);
      track.addEventListener('pointerup', this._onUp);
      track.addEventListener('pointercancel', this._onCancel);
    },

    destroy(): void {
      this._track?.removeEventListener('pointerdown', this._onDown);
      this._track?.removeEventListener('pointerup', this._onUp);
      this._track?.removeEventListener('pointercancel', this._onCancel);
    },

    // Keep two boxes on screen: the last reachable active index still leaves a
    // narrow neighbour to its right, so the active slide is never the last one.
    _maxIndex(): number {
      return Math.max(0, this.count - 2);
    },

    _apply(): void {
      const max = this._maxIndex();
      this.current = Math.max(0, Math.min(this.current, max));
      this._track?.style.setProperty('--ci', String(this.current));
      this._slides.forEach((slide, i) => slide.classList.toggle('is-current', i === this.current));
      this.atStart = this.current <= 0;
      this.atEnd = this.current >= max;
    },

    goTo(index: number): void {
      this.current = index;
      this._apply();
    },

    next(): void {
      this.goTo(this.current + 1);
    },

    prev(): void {
      this.goTo(this.current - 1);
    },

    isActive(index: number): boolean {
      return this.current === index;
    },
  };
}
